import { FunctionComponent, useState, useCallback, CSSProperties } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';

export interface ContentImageProps {
  /**
   * The URL of the image to display
   */
  src: string;

  /**
   * Alternative text for the image (required for accessibility)
   */
  altTag: string;

  /**
   * Optional title text displayed on hover
   */
  title?: string;

  /**
   * Optional width of the image
   */
  width?: string | number;

  /**
   * Optional height of the image
   */
  height?: string | number;
  /**
   * Optional aspect ratio for the image
   */

  aspectRatio?: string;

  /**
   * Optional test ID for testing
   */
  testId?: string;

  /**
   * Optional callback when image fails to load
   */
  onLoadError?: () => void;
}

const wrapperStyles = xcss({
  width: '100%',
  overflow: 'hidden',
  lineHeight: '0',
});

const spinnerContainerStyles = xcss({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
});

const imageContainerStyles = xcss({
  position: 'relative',
  width: '100%',
});

/**
 * Validates that the URL is safe to load (not a javascript: URL)
 */
const isValidImageUrl = (url: string): boolean => {
  try {
    // Check for javascript: protocol
    if (url.toLowerCase().startsWith('javascript:')) {
      return false;
    }

    // Check for data: URLs that might contain script
    if (url.toLowerCase().startsWith('data:')) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * ContentImage component displays an image with loading state and error handling.
 *
 * Features:
 * - Shows spinner while loading
 * - Fade transition when image loads
 * - Error state handling with optional callback
 * - URL security validation
 * - Responsive sizing with optional width/height
 *
 * @example
 * ```tsx
 * <ContentImage
 *   src="https://example.com/image.jpg"
 *   altTag="Example image"
 *   title="Hover text"
 *   width="100%"
 *   height="auto"
 *   onLoadError={() => console.log('Failed to load')}
 * />
 * ```
 */
export const ContentImage: FunctionComponent<ContentImageProps> = ({
  src,
  altTag,
  title,
  width,
  height,
  testId,
  onLoadError,
  aspectRatio,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Validate URL on mount/src change
  const isValidUrl = isValidImageUrl(src);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleImageError = useCallback(() => {
    setHasError(true);
    setIsLoading(false);
    onLoadError?.();
  }, [onLoadError]);

  // Return null if URL is invalid
  if (!isValidUrl) {
    return null;
  }

  // Return null if image failed to load
  if (hasError) {
    return null;
  }

  const imgStyles: CSSProperties = {
    width: width ? (typeof width === 'number' ? `${width}px` : width) : '100%',
    height: height
      ? typeof height === 'number'
        ? `${height}px`
        : height
      : 'auto',
    objectFit: 'cover',
    objectPosition: 'center',
    transition: 'opacity 200ms ease-in-out',
    opacity: isLoading ? 0 : 1,
    display: 'block',
    aspectRatio: aspectRatio,
  };

  return (
    <Box xcss={wrapperStyles} testId={testId}>
      <Box xcss={imageContainerStyles}>
        {isLoading && (
          <Box xcss={spinnerContainerStyles}>
            <Spinner interactionName="content-image-loading" />
          </Box>
        )}

        <img
          src={src}
          alt={altTag}
          title={title}
          onLoad={handleImageLoad}
          onError={handleImageError}
          // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
          style={imgStyles}
        />
      </Box>
    </Box>
  );
};

export default ContentImage;
